/**
 * Workshop FlightFrame — Apple Wallet style boarding pass.
 *
 * Authentic app layout inside, Denker glass outside. Content area has no
 * divider lines and no status dots.
 */

import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui";
import type { FrameStatus } from "@/workshop/components/shapes/shared/frame-utils";


interface FlightEndpoint {
  code: string;
  city: string;
  time: string;
}

interface FlightFrameProps {
  airline?: string;
  flightNo?: string;
  from?: FlightEndpoint;
  to?: FlightEndpoint;
  duration?: string;
  gate?: string;
  seat?: string;
  group?: string;
  pnr?: string;
  statusTag?: string;
  status?: FrameStatus;
  className?: string;
  "data-testid"?: string;
}

const MOCK_FROM: FlightEndpoint = { code: "BER", city: "Berlin", time: "08:45" };
const MOCK_TO: FlightEndpoint = { code: "JFK", city: "New York", time: "11:20" };

const MOCK_DEFAULTS: Required<
  Pick<
    FlightFrameProps,
    | "airline"
    | "flightNo"
    | "duration"
    | "gate"
    | "seat"
    | "group"
    | "pnr"
    | "statusTag"
  >
> = {
  airline: "Lufthansa",
  flightNo: "LH 402",
  duration: "8h 35m",
  gate: "B24",
  seat: "14A",
  group: "2",
  pnr: "XK7Q2M",
  statusTag: "On time",
};

export function FlightFrame({
  airline = MOCK_DEFAULTS.airline,
  flightNo = MOCK_DEFAULTS.flightNo,
  from = MOCK_FROM,
  to = MOCK_TO,
  duration = MOCK_DEFAULTS.duration,
  gate = MOCK_DEFAULTS.gate,
  seat = MOCK_DEFAULTS.seat,
  group = MOCK_DEFAULTS.group,
  pnr = MOCK_DEFAULTS.pnr,
  statusTag = MOCK_DEFAULTS.statusTag,
  status = "done",
  className,
  "data-testid": testId = "flight-frame",
}: FlightFrameProps) {
  const isEmpty = status === "done" && !flightNo;


  return (
    <div
      className={cn(
        surfaceRoleClassName("frame"),
        "group relative flex w-[320px] flex-col overflow-hidden rounded-frame",
        className,
      )}
      data-testid={testId}
      {...surfaceRoleAttributes("frame")}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
        {status === "streaming" && (
          <div
            className="flex flex-col gap-2"
            data-testid={`${testId}-loading`}
            aria-label="Loading flight details"
          >
            <div className="h-3 w-2/3 animate-pulse rounded bg-elevated/60" />
            <div className="h-8 w-full animate-pulse rounded bg-elevated/60" />
            <div className="grid grid-cols-4 gap-2">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-10 animate-pulse rounded bg-elevated/60"
                />
              ))}
            </div>
            <div className="flex items-center gap-2 text-canvas-xs text-muted">
              <Icons.Spinner
                className="h-3 w-3 animate-spin"
                aria-hidden="true"
              />
              Fetching flight details…
            </div>
          </div>
        )}

        {status === "error" && (
          <div
            className="flex flex-col items-center gap-2 rounded-preview bg-danger-muted px-3 py-5 text-center"
            data-testid={`${testId}-error`}
            role="alert"
          >
            <Icons.CircleAlert
              className="h-4 w-4 text-status-error"
              aria-hidden="true"
            />
            <p className="text-canvas-sm font-medium text-secondary">
              Couldn&apos;t load this boarding pass
            </p>
            <p className="text-canvas-xs text-muted">
              Check your connection and try again.
            </p>
          </div>
        )}

        {isEmpty && (
          <div
            className="flex flex-col items-center gap-2 px-3 py-6 text-center"
            data-testid={`${testId}-empty`}
          >
            <Icons.Send
              className="h-4 w-4 text-muted"
              aria-hidden="true"
            />
            <p className="text-canvas-sm font-medium text-secondary">
              No flight selected
            </p>
            <p className="text-canvas-xs text-muted">
              Ask for a trip and the boarding pass will appear here.
            </p>
          </div>
        )}

        {(status === "idle" || status === "done") && !isEmpty && (
          <>
            {/* Airline + flight no row */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 flex-col">
                <span
                  className="truncate text-canvas-sm font-semibold text-primary"
                  data-testid={`${testId}-airline`}
                >
                  {airline}
                </span>
                <span
                  className="text-canvas-xs text-muted"
                  data-testid={`${testId}-flight-no`}
                >
                  {flightNo}
                </span>
              </div>
              <span
                className="shrink-0 rounded-pill bg-success-muted px-2 py-0.5 text-canvas-xs font-medium text-success"
                data-testid={`${testId}-status-tag`}
              >
                {statusTag}
              </span>
            </div>

            {/* Route */}
            <div className="flex items-center gap-2">
              <div className="flex w-16 flex-col">
                <span
                  className="text-canvas-xl font-bold tracking-tight text-primary"
                  data-testid={`${testId}-from-code`}
                >
                  {from.code}
                </span>
                <span className="truncate text-canvas-xs text-muted">
                  {from.city}
                </span>
                <span
                  className="text-canvas-sm font-medium text-secondary"
                  data-testid={`${testId}-from-time`}
                >
                  {from.time}
                </span>
              </div>
              <div className="flex min-w-0 flex-1 flex-col items-center gap-1">
                <span className="text-canvas-2xs text-muted">{duration}</span>
                <div className="flex w-full items-center gap-1">
                  <span
                    className="h-px flex-1 bg-glass-stroke-light"
                    aria-hidden="true"
                  />
                  <span
                    className="text-canvas-base text-secondary"
                    aria-hidden="true"
                  >
                    ✈
                  </span>
                  <span
                    className="h-px flex-1 bg-glass-stroke-light"
                    aria-hidden="true"
                  />
                </div>
                <span className="text-canvas-2xs text-muted">Nonstop</span>
              </div>
              <div className="flex w-16 flex-col items-end text-right">
                <span
                  className="text-canvas-xl font-bold tracking-tight text-primary"
                  data-testid={`${testId}-to-code`}
                >
                  {to.code}
                </span>
                <span className="truncate text-canvas-xs text-muted">
                  {to.city}
                </span>
                <span
                  className="text-canvas-sm font-medium text-secondary"
                  data-testid={`${testId}-to-time`}
                >
                  {to.time}
                </span>
              </div>
            </div>

            {/* Meta grid */}
            <div className="grid grid-cols-4 gap-1.5">
              {[
                { label: "Gate", value: gate, id: "gate" },
                { label: "Seat", value: seat, id: "seat" },
                { label: "Group", value: group, id: "group" },
                { label: "PNR", value: pnr, id: "pnr" },
              ].map((cell) => (
                <div
                  key={cell.id}
                  className="flex flex-col rounded-control px-2 py-1.5"
                  data-testid={`${testId}-meta-${cell.id}`}
                >
                  <span className="text-canvas-2xs uppercase tracking-wide text-muted">
                    {cell.label}
                  </span>
                  <span className="font-mono text-canvas-sm font-semibold text-primary">
                    {cell.value}
                  </span>
                </div>
              ))}
            </div>

            {/* Barcode strip */}
            <div className="flex flex-col items-center gap-1 pt-1">
              <div
                className="h-10 w-full rounded-sm bg-[repeating-linear-gradient(90deg,var(--color-secondary)_0_2px,transparent_2px_4px,var(--color-secondary)_4px_5px,transparent_5px_9px)] opacity-70"
                role="img"
                aria-label={`Boarding pass barcode for ${pnr}`}
                data-testid={`${testId}-barcode`}
              />
              <span className="font-mono text-canvas-2xs tracking-[0.3em] text-muted">
                {pnr}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export type { FlightFrameProps, FlightEndpoint, FrameStatus };
